export const WORKFLOW_EMP_ROLE = "Operations";

/** Employee roles that get Admin-like portal access. */
export const ADMIN_LIKE_EMP_ROLES: readonly string[] = [
  "Admin",
  "Executive",
  "Operations",
  "Developer",
];

export const CRM_PORTAL_EMP_ROLES: readonly string[] = [
  "CRM",
  "Admin",
  "Executive",
  "Operations",
  "Developer",
];

/** Production portal — designer-family (Developer also allowed on Production). */
export const DESIGNER_PORTAL_EMP_ROLES: readonly string[] = [
  "Designer",
  "Senior Designer",
  "Captain",
  "Assistant Captain",
];

export const isDeveloperEmployee = (role?: string | null) =>
  String(role || "").trim().toLowerCase() === "developer";

/** Operations employees share CRM + Captain style access on both portals. */
export const isOperationsEmployee = (role?: string | null) =>
  String(role || "").trim().toLowerCase() === "operations";

/** Dual-portal employees (Production + Operations). */
export function isDualPortalEmployee(role?: string | null) {
  const n = String(role || "").trim().toLowerCase();
  return (
    isDeveloperEmployee(role) ||
    isOperationsEmployee(role) ||
    n === "admin" ||
    n === "super admin"
  );
}

/** Who may passkey-login on a given portal role. */
export function canEmployeeLoginOnPortal(
  empRole?: string | null,
  portalRole?: string | null,
) {
  const role = String(empRole || "").trim();
  if (!role || !portalRole) return false;
  if (portalRole === "Super Admin") return true;
  // Developer, Operations, Admin, Super Admin: Production and Operations.
  if (isDualPortalEmployee(role)) {
    return portalRole === "Production" || portalRole === "Operations";
  }
  if (portalRole === "Production") {
    return DESIGNER_PORTAL_EMP_ROLES.includes(role);
  }
  if (portalRole === "Operations") {
    return CRM_PORTAL_EMP_ROLES.includes(role);
  }
  return false;
}

export const SUPER_ADMIN_ASSIGNABLE_EMP_ROLES = [
  "Designer",
  "CRM",
  "Admin",
  "Executive",
  "Operations",
  "Developer",
  "Senior Designer",
  "Captain",
  "Assistant Captain",
  "Super Admin",
] as const;

export const HR_DIRECTORY_EMP_ROLES = [
  "Designer",
  "Senior Designer",
  "Captain",
  "Assistant Captain",
  "CRM",
  "Admin",
  "Executive",
  "Operations",
  "Developer",
  "Super Admin",
] as const;

export const isWorkflowEmployee = (role?: string | null) =>
  String(role || "").trim() === WORKFLOW_EMP_ROLE;

export const isAdminLikeEmployee = (role?: string | null) =>
  ADMIN_LIKE_EMP_ROLES.includes(String(role || "").trim());

/** Employee roles that can open the team attendance report. */
export const ATTENDANCE_REPORT_EMP_ROLES: readonly string[] = [
  "Admin",
  "Executive",
  "Super Admin",
];

/**
 * HR / team-dashboard admins.
 * Developer / Operations / CRM / Designer / Captain are self-only on employee dashboard.
 */
export const isHrAdminEmployee = (role?: string | null) => {
  const r = String(role || "").trim().toLowerCase();
  return r === "admin" || r === "executive" || r === "super admin";
};

/** Design-side roles shown first on employee overview. */
export const OVERVIEW_PRIMARY_EMP_ROLES = [
  "Designer",
  "Senior Designer",
  "Assistant Captain",
  "Captain",
  "Super Admin",
] as const;

/** Shown under a secondary section on employee overview. */
export const OVERVIEW_SECONDARY_EMP_ROLES = ["CRM", "Admin"] as const;

export type OverviewEmployeeGroup = "primary" | "secondary" | "other";

export function overviewEmployeeGroup(
  role?: string | null,
): OverviewEmployeeGroup {
  const r = String(role || "").trim();
  if ((OVERVIEW_PRIMARY_EMP_ROLES as readonly string[]).includes(r)) {
    return "primary";
  }
  if ((OVERVIEW_SECONDARY_EMP_ROLES as readonly string[]).includes(r)) {
    return "secondary";
  }
  return "other";
}

/** Super Admin user, or Admin / Executive / Super Admin employees, can open any overview. */
export const canViewEmployeeOverviewAll = (
  portalRole?: string | null,
  empRole?: string | null,
) => isSuperAdminAccess(portalRole, empRole) || isHrAdminEmployee(empRole);

/** Portal Super Admin user OR employee role Super Admin — same access everywhere. */
export const isSuperAdminAccess = (
  portalRole?: string | null,
  empRole?: string | null,
) =>
  portalRole === "Super Admin" ||
  String(empRole || "").trim().toLowerCase() === "super admin";

export const canViewAttendanceReport = (
  portalRole?: string | null,
  empRole?: string | null,
) =>
  isSuperAdminAccess(portalRole, empRole) ||
  ATTENDANCE_REPORT_EMP_ROLES.includes(String(empRole || "").trim());

/** Super Admin, Admin, Executive can mark attendance from portal. */
export const canManageAttendance = (
  portalRole?: string | null,
  empRole?: string | null,
) => {
  if (isSuperAdminAccess(portalRole, empRole)) return true;
  const role = String(empRole || "").trim().toLowerCase();
  return role === "admin" || role === "executive";
};

/** Lab pricing pages: Super Admin portal, or CRM portal with Super Admin / Executive employee. */
export const LAB_PRICING_EMP_ROLES: readonly string[] = [
  "Super Admin",
  "Executive",
];

export const canAccessLabPricing = (
  portalRole?: string | null,
  empRole?: string | null,
) =>
  portalRole === "Super Admin" ||
  (portalRole === "Operations" &&
    LAB_PRICING_EMP_ROLES.includes(String(empRole || "").trim()));

/** Case Report: Super Admin portal, or Operations portal with these employee roles. */
export const CASE_REPORT_EMP_ROLES: readonly string[] = [
  "Super Admin",
  "Admin",
  "Executive",
  "Developer",
];

export const canAccessCaseLifecycleReport = (
  portalRole?: string | null,
  empRole?: string | null,
) =>
  portalRole === "Super Admin" ||
  (portalRole === "Operations" &&
    CASE_REPORT_EMP_ROLES.includes(String(empRole || "").trim()));

/** Photo Recolour studio: Operations portal + these employee roles only. */
export const PHOTO_RECOLOUR_EMP_ROLES: readonly string[] = [
  "CRM",
  "Admin",
  "Executive",
  "Super Admin",
];

/**
 * Photo Recolour access:
 * - Super Admin portal (always)
 * - Verified Photo Recolour Users
 * - Operations portal when employee is CRM / Admin / Executive / Super Admin
 */
export const canAccessPhotoRecolour = (
  portalRole?: string | null,
  empRole?: string | null,
  verifyPhotoRecolour?: boolean,
) => {
  if (portalRole === "Super Admin") return true;
  if (portalRole === "User" && !!verifyPhotoRecolour) return true;
  if (portalRole === "Operations") {
    return PHOTO_RECOLOUR_EMP_ROLES.includes(String(empRole || "").trim());
  }
  return false;
};

/** Internal staff do not spend Photo Recolour credits. */
export const canUsePhotoRecolourWithoutCredits = (
  portalRole?: string | null,
  empRole?: string | null,
) =>
  portalRole === "Super Admin" ||
  (portalRole === "Operations" &&
    PHOTO_RECOLOUR_EMP_ROLES.includes(String(empRole || "").trim()));
